import { useCallback, useState } from 'react';

import Button from '@/componentsv2/Button';
import { ERROR_CODES } from '@/core/api';

import { STEP_COPY } from '../constants';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';

// Step reached when resolveEmailOtpVerification (EmailOtpStep) resolves with
// a pending project invite — a pending invite always short-circuits the
// team-create/subscription branch (see the reducer's resolveEmailOtpVerification
// case in useSelfServiceSMBSignupFlow.ts). Joining a project via an existing
// invite is free, so this step's only job is to confirm + accept; there's no
// billing step to fall through to afterward.

interface InviteAcceptStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  // Accepts the pending invite using the freshly minted invite JWT in
  // flow.state.pendingInvite.token. On success the caller (see
  // onAcceptInvite in useSelfServiceSMBSignupModal.tsx) navigates the user
  // to the project and dismisses the modal — there's no further step in
  // this flow to advance to.
  onAcceptInvite: (token: string) => Promise<void>;
}

export function InviteAcceptStep({ flow, onAcceptInvite }: InviteAcceptStepProps): JSX.Element {
  const { state } = flow;
  const [busy, setBusy] = useState(false);
  const invite = state.pendingInvite;
  const projectName = invite?.projectName ?? 'the project';
  const inviterName = invite?.inviterName ?? null;

  const accept = useCallback(async () => {
    if (!invite || busy) return;
    try {
      setBusy(true);
      flow.clearError();
      await onAcceptInvite(invite.token);
    } catch (err) {
      flow.setError(
        ERROR_CODES.UNKNOWN,
        err instanceof Error ? err.message : 'Unable to join the project. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  }, [invite, busy, flow, onAcceptInvite]);

  // Escape hatch for a user matched to the wrong/unwanted invite. Continues
  // signup instead of dead-ending back at OTP entry: declineInvite drops
  // the invite and routes into the same team-create/team-join branching
  // the domain-match check would have used had no invite been found (see
  // the reducer's declineInvite case).
  const handleDecline = useCallback(() => flow.declineInvite(), [flow]);

  return (
    <>
      <div className="flex flex-col gap-16 px-24 py-16">
        <header>
          <h2
            id="self-service-smb-invite-accept-title"
            className="text-18 font-semibold text-on-surface"
          >
            {STEP_COPY.inviteAccept.titlePrefix} <em className="italic">{projectName}</em>
          </h2>
          <p className="mt-4 text-14 text-on-surface-muted">
            {STEP_COPY.inviteAccept.subtitle(inviterName)}
          </p>
        </header>
        {state.error && (
          <p role="alert" className="text-14 text-alert-error">
            {state.error.message}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-8 border-t border-solid border-surface-border bg-surface-elevated px-24 py-16">
        <Button variant="tertiary" size="large" onClick={handleDecline} disabled={busy}>
          {STEP_COPY.inviteAccept.goBack}
        </Button>
        <Button size="large" onClick={accept} disabled={busy || !invite}>
          {busy ? STEP_COPY.inviteAccept.submitBusy : STEP_COPY.inviteAccept.submit()}
        </Button>
      </div>
    </>
  );
}
